import React, { useState, useEffect, useMemo } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const COLORS = [
  '#c9a96e', '#6366f1', '#22c55e', '#ef4444', '#3b82f6',
  '#f59e0b', '#ec4899', '#14b8a6', '#8b5cf6', '#f97316',
];

const PERIODS = [
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: '3Y', months: 36 },
  { label: '5Y', months: 60 },
  { label: 'All', months: null },
];

const fmtPct = (v) => (v != null ? (v * 100).toFixed(1) + '%' : 'N/A');
const fmtNum = (v) => (v != null ? (typeof v === 'number' ? v.toFixed(2) : v) : 'N/A');
const valColor = (v) => (v > 0 ? 'text-green-400' : v < 0 ? 'text-red-400' : 'text-gray-400');

const ComparisonPage = ({ analyticsParams, paramsKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scatterPeriod, setScatterPeriod] = useState(null); // null = All

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchFn = () => tradingService.analyticsComparison(analyticsParams);
        const res = await fetchFn();
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [paramsKey]);

  // Cumulative returns chart
  const cumTraces = useMemo(() => {
    if (!data?.cumulative) return [];
    const { index: dates, columns: names, data: rows } = data.cumulative;

    return names.map((name, ci) => {
      const xArr = [], yArr = [];
      dates.forEach((d, i) => {
        if (rows[i][ci] != null) { xArr.push(d); yArr.push(rows[i][ci]); }
      });
      const isBenchmark = name === 'SPX Index' || name === 'SPY';
      return {
        x: xArr, y: yArr, type: 'scatter', mode: 'lines', name,
        line: {
          color: isBenchmark ? '#DC143C' : COLORS[ci % COLORS.length],
          width: isBenchmark ? 2 : 1.5,
          dash: isBenchmark ? 'dash' : 'solid',
        },
        hovertemplate: `${name}: %{y:.2%}<extra></extra>`,
      };
    });
  }, [data]);

  // Risk-return scatter
  const scatterTraces = useMemo(() => {
    if (!data?.scatter) return [];
    let points = data.scatter;

    // Filter by period if applicable
    if (scatterPeriod && data.scatter_periods) {
      const key = { 6: '6m', 12: '1y', 36: '3y', 60: '5y' }[scatterPeriod];
      points = data.scatter_periods?.[key] || data.scatter;
    }
    if (!points || !points.length) return [];

    const strategies = points.filter((s) => s.fund !== 'SPX Index' && s.fund !== 'SPY');
    const benchmarks = points.filter((s) => s.fund === 'SPX Index' || s.fund === 'SPY');

    const traces = [];
    if (strategies.length > 0) {
      traces.push({
        x: strategies.map((s) => s.risk),
        y: strategies.map((s) => s.return),
        text: strategies.map((s) => s.fund),
        type: 'scatter', mode: 'markers+text',
        textposition: 'top center', textfont: { size: 10 },
        marker: { color: strategies.map((_, i) => COLORS[i % COLORS.length]), size: 12 },
        hovertemplate: '%{text}<br>Risk: %{x:.2%}<br>Return: %{y:.2%}<extra></extra>',
        showlegend: false,
      });
    }
    benchmarks.forEach((bm) => {
      traces.push({
        x: [bm.risk], y: [bm.return], text: [bm.fund],
        type: 'scatter', mode: 'markers+text',
        textposition: 'top center', textfont: { size: 10, color: '#DC143C' },
        marker: { color: '#DC143C', size: 14, symbol: 'diamond' },
        hovertemplate: `${bm.fund}<br>Risk: %{x:.2%}<br>Return: %{y:.2%}<extra></extra>`,
        showlegend: false,
      });
    });
    return traces;
  }, [data, scatterPeriod]);

  // Stats table
  const statsTable = useMemo(() => {
    if (!data?.stats) return null;
    return data.stats;
  }, [data]);

  const isPctCol = (c) => {
    const l = c.toLowerCase();
    return l.includes('return') || l.includes('drawdown') || l.includes('vol')
      || l.includes('std') || l.includes('%') || l.includes('best') || l.includes('worst');
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  return (
    <div>
      {/* Cumulative Returns */}
      {cumTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Cumulative Returns Comparison</h3>
          <PlotlyChart
            data={cumTraces}
            layout={{
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              yaxis: { title: 'Cumulative Return', tickformat: '.0%' },
              xaxis: { title: '' },
              legend: { orientation: 'h', yanchor: 'bottom', y: -0.25, xanchor: 'center', x: 0.5 },
            }}
            style={{ minHeight: 350 }}
          />
        </div>
      )}

      {/* Risk-Return Scatter */}
      {scatterTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Risk-Return Profile</h3>
            <div className="flex gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => setScatterPeriod(p.months)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    scatterPeriod === p.months
                      ? 'bg-brand-gold text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <PlotlyChart
            data={scatterTraces}
            layout={{
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              xaxis: { title: 'Annualized Risk (Std Dev)', tickformat: '.1%', rangemode: 'tozero' },
              yaxis: { title: 'Annualized Return', tickformat: '.1%', rangemode: 'tozero' },
              hovermode: 'closest',
            }}
            style={{ minHeight: 350 }}
          />
        </div>
      )}

      {/* Stats Table */}
      {statsTable && statsTable.index && statsTable.index.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Comparison Statistics</h3>
          <div className="overflow-x-auto scrollbar-thin rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 text-left sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50">Strategy</th>
                  {statsTable.columns.map((col) => (
                    <th key={col} className="px-3 py-2.5 text-right whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {statsTable.index.map((name, ri) => (
                  <tr key={name} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-800 dark:text-gray-200 sticky left-0 bg-white dark:bg-[#161b22]">{name}</td>
                    {statsTable.data[ri].map((val, ci) => (
                      <td key={ci} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${val != null ? valColor(val) : 'text-gray-400'}`}>
                        {val == null ? 'N/A' : isPctCol(statsTable.columns[ci]) ? fmtPct(val) : fmtNum(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonPage;
